import useAuthStore from "~/store/auth.store";
import useSidebarStore from "~/store/sidebar.store";
import { UserRole } from "~/shared/config";
import { useEffect, useState, useCallback } from "react";
import networkService from "~/services/network.service";
import { toast } from "react-toastify";
import { Download, Edit2, CreditCard } from "lucide-react";
import { DescriptionIcon } from "~/shared/icon/description-icon";
import { PaymentsIcon } from "~/shared/icon/payments-icon";
import { OpenInNewIcon } from "~/shared/icon/open-in-new";
import { US_STATES } from "../purchase/page";

const BillingPage = () => {
    const [collapsed] = useSidebarStore((state) => [state.collapsed]);
    const { user, accessToken } = useAuthStore((state) => ({
        user: state.user,
        accessToken: state.accessToken,
    }));
    const [billing, setBilling] = useState<any>(null);
    const [invoices, setInvoices] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingInvoices, setLoadingInvoices] = useState(true);
    const [paymentMethod, setPaymentMethod] = useState<any>(null);
    const [loadingPayment, setLoadingPayment] = useState(true);

    const handleEditBilling = useCallback(async () => {
        if (!user?.organizationId) return;
        try {
            const returnUrl = `${window.location.origin}/billing`;
            const res = await networkService.post<any>(
                `/organizations/${user.organizationId}/stripe/billing-portal`,
                { returnUrl }
            );
            const portalUrl = res.data?.portalUrl;
            if (portalUrl) {
                window.location.href = portalUrl;
            } else {
                toast.error("Could not get Stripe portal URL");
            }
        } catch (e) {
            toast.error("Failed to open Stripe billing portal");
        }
    }, [user?.organizationId]);

    useEffect(() => {
        const fetchBilling = async () => {
            if (!user?.organizationId) return;
            try {
                setLoading(true);
                const res = await networkService.get<any>(`/organizations/${user.organizationId}/billing-details`);
                setBilling(res.data);
            } catch (e) {
                // Silently handle the error for new users without billing details
                setBilling(null);
            } finally {
                setLoading(false);
            }
        };
        fetchBilling();
    }, [user?.organizationId]);

    useEffect(() => {
        const fetchInvoices = async () => {
            if (!user?.organizationId) return;
            try {
                setLoadingInvoices(true);
                const res = await networkService.get<any>(`/invoices/organization/${user.organizationId}`);
                // Filter out invoices with totalAmount === 0 and sort by createdAt descending
                const filteredSorted = (res.data.results || [])
                  .filter(inv => inv.totalAmount !== 0)
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                setInvoices(filteredSorted);
            } catch (e) {
                toast.error("Failed to fetch invoices");
            } finally {
                setLoadingInvoices(false);
            }
        };
        fetchInvoices();
    }, [user?.organizationId]);

    useEffect(() => {
        const fetchPaymentMethod = async () => {
            if (!user?.organizationId) return;
            try {
                setLoadingPayment(true);
                const res = await networkService.get<any>(`/organizations/${user.organizationId}/payment-method`);
                setPaymentMethod(res.data);
            } catch (e) {
                setPaymentMethod(null);
            } finally {
                setLoadingPayment(false);
            }
        };
        fetchPaymentMethod();
    }, [user?.organizationId]);

    return (
        <div
            className={`font-space-grotesk flex flex-col flex-grow px-10 py-12 mb-2 ${collapsed ? `${user?.role === UserRole.Admin ? "ml-20" : "md:ml-20"}` : `${user?.role === UserRole.Admin ? "ml-80" : "md:ml-80"}`} transition-all duration-300 pb-24`}
        >
            <h1 className="text-3xl font-bold mb-8">Billing</h1>
            {/* Overview */}
            <div className="text-lg font-semibold mb-4 pl-6">Overview</div>
            <div className="bg-white rounded-md border border-gray-200 p-6 mb-10 w-full max-w-full">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-6">
                    {/* Billing Info */}
                    <div className="flex-1 min-w-[220px]">
                        <div className="font-semibold mb-2 flex items-center gap-2">
                            <span className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center mr-1">
                                <DescriptionIcon width={19} height={19} />
                            </span>
                            <span className="text-base">Billing information</span>
                        </div>
                        {loading ? (
                            <div className="text-gray-400 text-sm">Loading...</div>
                        ) : billing ? (
                            <div className="text-base">
                                <div className="font-semibold">{billing.name || ""}</div>
                                <div className="font-semibold text-base">{billing.legalName || ""}</div>
                                <div className="">{billing.addressLine1 || ""}{billing.addressLine2 && `, ${billing.addressLine2}`}</div>
                                <div className="">
                                    {billing.city || ""}
                                    {billing.state ? `, ${US_STATES.find(s => s.name === billing.state || s.code === billing.state)?.code || billing.state}` : ""}
                                    {billing.zipcode ? ` ${billing.zipcode}` : ""}
                                </div>
                                <div className="mb-1">United States</div>
                            </div>
                        ) : (
                            <div className="text-sm">-</div>
                        )}
                    </div>
                    {/* Payment Info */}
                    <div className="flex-1 min-w-[220px]">
                        <div className="font-semibold mb-2 flex items-center gap-2">
                            <span className="bg-gray-100 rounded-full flex items-center justify-center mr-1">
                                <PaymentsIcon width="25" height="26" />
                            </span>
                            <span className="text-base">
                                Payment
                            </span>
                        </div>
                        <div className="text-base text-gray-700">
                            {loadingPayment ? (
                                <span>Loading...</span>
                            ) : paymentMethod ? (
                                <>
                                    {paymentMethod.last4 ? `Card ending by **** ${paymentMethod.last4}` : '-'}<br />
                                    {paymentMethod.nextPayment?.date ? `Next payment: ${new Date(paymentMethod.nextPayment.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}` : ''}
                                </>
                            ) : (
                                <span>No payment method</span>
                            )}
                        </div>
                    </div>
                    {/* Edit Button */}
                    <div className="text-base font-semibold flex justify-center items-center bg-angular-gradient rounded-full p-0.5">
                        <button
                            type="submit"
                            className="w-full flex items-center justify-center gap-3 font-semibold bg-white-a700 rounded-full px-5 py-2 hover:bg-light-angular-gradient"
                            onClick={handleEditBilling}
                        >
                            <OpenInNewIcon />
                            Edit
                        </button>
                    </div>
                </div>
            </div>
            {/* Invoices */}
            <div className="w-full max-w-full">
                <div className="font-semibold text-lg mb-4 pl-6">Invoices</div>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Subscription type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Number of licenses</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice total</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Status</th>
                                <th className="px-6 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loadingInvoices ? (
                                <tr><td colSpan={6} className="text-center py-6 text-gray-400">Loading...</td></tr>
                            ) : invoices.length === 0 ? (
                                <tr><td colSpan={6} className="py-6 text-start pl-6 text-sm">No invoice yet</td></tr>
                            ) : invoices.map((inv, idx) => {
                                // 1. Due Date: subscription.periodEndDate
                                const dueDate = inv.subscription?.periodEndDate ? new Date(inv.subscription.periodEndDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '-';
                                // 2. Subscription Type
                                let subType = '-';
                                const interval = inv.subscription?.plan?.pricingModel?.interval;
                                const intervalCount = inv.subscription?.plan?.pricingModel?.intervalCount;
                                if (interval && intervalCount) {
                                    if (interval === 'year') {
                                        subType = `${intervalCount} year${intervalCount > 1 ? 's' : ''}`;
                                    } else if (interval === 'month') {
                                        subType = `${intervalCount} month${intervalCount > 1 ? 's' : ''}`;
                                    } else {
                                        subType = `${intervalCount} ${interval}`;
                                    }
                                }
                                // 3. Number of Licenses
                                const numLicenses = inv.metaData?.itemCount || '-';
                                // 4. Invoice Total
                                const invoiceTotal = typeof inv.totalAmount === 'number' ? `$${(inv.totalAmount / 100).toFixed(2)}` : '-';
                                // 5. Status
                                const status = inv.status ? inv.status.charAt(0).toUpperCase() + inv.status.slice(1).toLowerCase() : '-';
                                // 6. Download
                                const fileUrl = inv.fileUrl;
                                return (
                                    <tr key={inv.id || idx}>
                                        <td className="px-6 py-4 whitespace-nowrap">{dueDate}</td>
                                        <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">{subType}</td>
                                        <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">{numLicenses}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{invoiceTotal}</td>
                                        <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${status === 'Paid' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{status}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            {fileUrl && (
                                                <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-indigo-600" title="Download invoice">
                                                    <Download size={18} />
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default BillingPage;